import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import loginImage from "../static/media/Login/login.webp";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const initialEmail = useMemo(() => {
    return location.state?.email || "";
  }, [location.state]);

  const [form, setForm] = useState({
    email: initialEmail,
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({
    type: "",
    message: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      setBanner({
        type: "success",
        message: location.state.message,
      });
    }
  }, [location.state]);

  const clearMessages = () => {
    if (banner.message) {
      setBanner({ type: "", message: "" });
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
      general: "",
    }));

    clearMessages();
  };

  const validateLogin = () => {
    const newErrors = {};

    if (!form.email.trim()) {
      newErrors.email = "Ingresa tu correo electrónico";
    } else if (!emailRegex.test(form.email.trim())) {
      newErrors.email = "Ingresa un correo válido";
    }

    if (!form.password) {
      newErrors.password = "Ingresa tu contraseña";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateLogin()) return;

    setLoading(true);
    setBanner({ type: "", message: "" });

    try {
      const response = await fetch("/api/gym/login/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.error || "No se pudo iniciar sesión";

        setErrors((prev) => ({
          ...prev,
          general: message,
        }));

        setBanner({
          type: "error",
          message,
        });
        return;
      }

      login(data.user, data.access, data.refresh);
      navigate("/mi-perfil");
    } catch {
      const message = "Ocurrió un error al conectar con el servidor";

      setErrors((prev) => ({
        ...prev,
        general: message,
      }));

      setBanner({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = form.email.trim().toLowerCase();

    clearMessages();

    if (!email) {
      setErrors((prev) => ({
        ...prev,
        email: "Ingresa tu correo para recuperar tu contraseña",
      }));
      return;
    }

    if (!emailRegex.test(email)) {
      setErrors((prev) => ({
        ...prev,
        email: "Ingresa un correo válido",
      }));
      return;
    }

    setForgotLoading(true);

    try {
      const response = await fetch("/api/gym/forgot-password/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setBanner({
          type: "error",
          message: data.error || "No se pudo enviar el código",
        });
        return;
      }

      navigate("/reset-password", {
        state: {
          email,
          message: "Te enviamos un código de recuperación a tu correo electrónico",
        },
      });
    } catch {
      setBanner({
        type: "error",
        message: "Ocurrió un error al conectar con el servidor",
      });
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Iniciar sesión | NovaXFit</title>
        <meta
          name="description"
          content="Inicia sesión en tu cuenta NovaXFit para revisar tu perfil, membresías activas, código QR y beneficios disponibles para usuarios del gimnasio."
        />
        <link rel="canonical" href="https://novaxfit.com/login" />
      </Helmet>

      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: "#ffffff",
          py: { xs: 5, md: 7 },
        }}
      >
        <Container
          maxWidth={false}
          sx={{ width: { xs: "90%", md: "82%" }, maxWidth: "1380px", mx: "auto" }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gridTemplateRows: { xs: "auto auto", lg: "auto 1fr" },
              gap: { xs: 4, md: 4.5, lg: 5 },
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                gridColumn: { xs: "1", lg: "1 / span 2" },
                textAlign: "center",
                maxWidth: 860,
                mx: "auto",
                mb: { xs: 0.5, md: 1.2 },
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  px: 1.5,
                  py: 0.7,
                  borderRadius: 999,
                  bgcolor: "rgba(220,38,38,0.08)",
                  border: "1px solid rgba(220,38,38,0.12)",
                  mb: 1.8,
                }}
              >
                <Typography
                  sx={{
                    color: "#dc2626",
                    fontWeight: 800,
                    fontSize: { xs: 12.8, md: 13.2 },
                    letterSpacing: "0.02em",
                  }}
                >
                  ACCESO NOVAXFIT
                </Typography>
              </Box>

              <Typography
                component="h1"
                sx={{
                  color: "#111111",
                  fontWeight: 800,
                  lineHeight: 1.02,
                  letterSpacing: -1.2,
                  fontSize: {
                    xs: "clamp(34px, 8vw, 44px)",
                    md: "clamp(42px, 3.4vw, 58px)",
                  },
                  mb: 1.5,
                }}
              >
                Inicia sesión en tu cuenta NovaXFit
              </Typography>

              <Typography
                sx={{
                  color: "rgba(17,17,17,0.72)",
                  lineHeight: 1.8,
                  fontSize: { xs: 15.5, md: 16.5 },
                  fontWeight: 500,
                  maxWidth: 720,
                  mx: "auto",
                }}
              >
                Accede a tu cuenta para ver tu perfil, revisar tus membresías activas y
                aprovechar tus beneficios con aliados de{" "}
                <Box component="strong" sx={{ fontWeight: 800, color: "#111111" }}>
                  NovaXFit
                </Box>
                .
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.2, sm: 1.4 }}
                sx={{
                  mt: 2.4,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    px: 1.4,
                    py: 1,
                    borderRadius: "14px",
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(17,17,17,0.08)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  }}
                >
                  <WorkspacePremiumRoundedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 14.2, fontWeight: 800, color: "#111111" }}>
                    Membresías activas
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    px: 1.4,
                    py: 1,
                    borderRadius: "14px",
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(17,17,17,0.08)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  }}
                >
                  <BadgeRoundedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 14.2, fontWeight: 800, color: "#111111" }}>
                    Perfil y datos
                  </Typography>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    px: 1.4,
                    py: 1,
                    borderRadius: "14px",
                    bgcolor: "#ffffff",
                    border: "1px solid rgba(17,17,17,0.08)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  }}
                >
                  <LocalOfferRoundedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 14.2, fontWeight: 800, color: "#111111" }}>
                    Beneficios y aliados
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", lg: "flex-end" },
                alignItems: "center",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: { xs: 220, md: 340, lg: 400 },
                  mx: "auto",
                  borderRadius: { xs: "26px", md: "30px" },
                  overflow: "hidden",
                  background:
                    "linear-gradient(135deg, #090909 0%, #161616 34%, #2a0a0d 68%, #0d0d0d 100%)",
                  border: "1px solid rgba(17,17,17,0.08)",
                  boxShadow: "0 28px 70px rgba(0,0,0,0.12)",
                  minHeight: { xs: 300, md: 330, lg: 380 },
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "radial-gradient(circle at top right, rgba(220,38,38,0.22) 0%, transparent 32%), radial-gradient(circle at bottom left, rgba(255,255,255,0.06) 0%, transparent 26%)",
                    pointerEvents: "none",
                  }}
                />
                <motion.img
                  src={loginImage}
                  alt="Coach NovaXFit"
                  initial={{ opacity: 0, y: 28, scale: 0.97 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7 }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", lg: "flex-start" },
                width: "100%",
                minHeight: { xs: "auto", md: 350, lg: 400 },
              }}
            >
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  width: "100%",
                  maxWidth: 460,
                  borderRadius: { xs: "24px", md: "28px" },
                  border: "1px solid rgba(17,17,17,0.08)",
                  bgcolor: "#ffffff",
                  boxShadow: "0 24px 70px rgba(0,0,0,0.08)",
                  p: { xs: 2.2, sm: 3, md: 3.2 },
                }}
              >
                <Stack spacing={2}>
                  {banner.message && (
                    <Alert
                      severity={banner.type}
                      sx={{
                        borderRadius: "14px",
                        "& .MuiAlert-message": {
                          fontWeight: 600,
                        },
                      }}
                    >
                      {banner.message}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    error={Boolean(errors.email)}
                    helperText={errors.email || " "}
                    variant="outlined"
                    autoComplete="email"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Contraseña"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange("password")}
                    error={Boolean(errors.password)}
                    helperText={errors.password || " "}
                    variant="outlined"
                    autoComplete="current-password"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            onClick={() => setShowPassword((prev) => !prev)}
                            sx={{ color: "#111111" }}
                            aria-label={
                              showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                            }
                          >
                            {showPassword ? (
                              <VisibilityOffRoundedIcon />
                            ) : (
                              <VisibilityRoundedIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: -0.5,
                    }}
                  >
                    <Link
                      component="button"
                      type="button"
                      underline="none"
                      onClick={handleForgotPassword}
                      disabled={forgotLoading}
                      sx={{
                        color: "#dc2626",
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: forgotLoading ? "default" : "pointer",
                        opacity: forgotLoading ? 0.7 : 1,
                        "&:hover": {
                          color: "#b91c1c",
                        },
                      }}
                    >
                      {forgotLoading ? "Enviando código..." : "¿Olvidaste tu contraseña?"}
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      mt: 0.5,
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: "16px",
                      py: 1.25,
                      fontSize: "1rem",
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg, #5f0008 0%, #a80d19 35%, #df2632 62%, #161616 100%)",
                      boxShadow: "0 18px 50px rgba(220,38,38,0.22)",
                      border: "1px solid rgba(0,0,0,0.08)",
                      "&:hover": {
                        filter: "brightness(1.05)",
                        boxShadow: "0 22px 56px rgba(220,38,38,0.26)",
                      },
                      "&.Mui-disabled": {
                        color: "#ffffff",
                        opacity: 0.75,
                      },
                    }}
                  >
                    {loading ? "Ingresando..." : "Iniciar sesión"}
                  </Button>

                  <Typography
                    sx={{
                      textAlign: "center",
                      color: "rgba(17,17,17,0.72)",
                      fontSize: 15,
                      fontWeight: 500,
                    }}
                  >
                    ¿No tienes una cuenta?{" "}
                    <Link
                      component={RouterLink}
                      to="/registro"
                      underline="none"
                      sx={{
                        color: "#dc2626",
                        fontWeight: 800,
                        "&:hover": {
                          color: "#b91c1c",
                        },
                      }}
                    >
                      Regístrate
                    </Link>
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}